import {useCallback, useEffect, useState} from 'react'
import {
  Badge,
  Breadcrumb,
  Button,
  ButtonGroup,
  Col,
  Dropdown,
  Form,
  InputGroup,
  ListGroup,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap'
import Select from 'react-select'
import {FormattedMessage, useIntl} from 'react-intl'
import {useDisclourse} from '../../hooks/useDisclourse'
import {Tooltip} from 'react-tooltip'
import {ModalAddEditContact} from './components/ModalAddEditContact'
import {ModalCommunicationLogs} from './components/ModalCommunicationLogs'
import {SendMessageCanvas} from '../../components/messaging/SendMessageCanvas'
import {useLazyQuery, useMutation, useApolloClient, useQuery} from '@apollo/client'
import {GET_CONTACTS_PAGE_API} from '../../gql/queries/contactsApiQueries'
import {GET_ALL_POLICIES_STATUSES} from '../../gql/queries/policiesQueries'
import {GET_ALL_CARRIERS_LIST} from '../../gql/queries/carriersQuery'
import {GET_ALL_SIGNATURE_FORMS_ACTIVE} from '../../gql/queries/signatureFormsApiQueries'
import {GET_ALL_STATES_EU_LIST}  from '../../gql/queries/statesEUQuery'
import {GET_ASSIGNMENT_USERS_LIST} from '../../gql/queries/usersQuery'
import {INITIAL_CONTACT_FILTERS, IMMIGRATION_STATUS_CHOICES} from '../../helpers/consts'
import {toast} from 'react-toastify'
import {IResContactsAPI, NodeContact} from '../../types/contactsApi'
import moment from 'moment'
import {SIZE_PAGE_DEFAULT} from '../../helpers/consts'
import {PaginationUtils} from '../shared/PaginationUtils'
import {
  ANALYZE_DUPLICATE_CONTACTS,
  DELETE_CONTACT,
  EDIT_CONTACT,
} from '../../gql/mutations/contactsMutation'
import {IResCreateContactApi} from '../../types/contacts'
import {PaginationSection} from '../shared/PaginationSection'
import {ModalPreview} from './components/ModalPreview'
import {ContactPolicyTypes} from './components/ContactPolicyTypes'
import {ModalRequestSignature} from '../signatureForms/components/ModalRequestSignature'
import {ModalAddPolice} from '../policies/components/ModalAddPolice'
import {ModalFilters} from './components/ModalFilters'
import {ModalAssignContact} from './components/ModalAssignContact'
import {AppliedFiltersList} from '../policies/components/AppliedFiltersList'
import {TagManager} from '../recipients/components/TagManager'
import {BulkTagModal} from '../recipients/components/BulkTagModal'
import {ContactFilters, ContactSavedFilter} from './contactsTypes'
import {
  hasPermission,
  isAdministrator,
  parseId,
  getTagContrastColor,
} from '../../../../app/tfi/helpers'
import {ALL_PERMISSIONS} from '../../../../app/tfi/helpers/consts'
import {GENDER_OPTIONS} from '../../helpers/catalogs'
import {GET_CONTACT_TAGS} from '../../gql/queries/tags'
import {useGetUser} from '../../../../app/tfi/store/userStore'
import {useStaticCatalog} from '../../hooks/useStaticCatalog'
import {GET_ALL_ORGANIZATIONS} from '../../gql/queries/organizationsQuery'
import {GET_MY_CONTACT_FILTERS} from '../../gql/queries/contactFiltersQueries'
import {
  CREATE_CONTACT_FILTER,
  DELETE_CONTACT_FILTER,
  UPDATE_CONTACT_FILTER,
} from '../../gql/mutations/contactFilterMutations'
import {PhoneProviderActions} from '../../components/telephony/PhoneProviderActions'
import {UserAvatar} from '../../components/UserAvatar'
import {AgentInfoTooltip} from '../shared/AgentInfoTooltip'
import {
  MergeContactCandidate,
  ModalMergeContacts,
} from './components/ModalMergeContacts'

export const useLoadFilterData = (enabled: boolean) => {
  const intl = useIntl()
  const permissions = useGetUser((user) => user?.user?.permissions) ?? []
  const client = useApolloClient()

  const fetchUsers = useCallback(
    () =>
      client
        .query({
          query: GET_ASSIGNMENT_USERS_LIST,
          variables: {assignmentCandidates: false},
          fetchPolicy: 'network-only',
        })
        .then((res) => res.data),
    [client]
  )
  const usersCatalog = useStaticCatalog('contacts-users-basic', enabled, fetchUsers)

  const fetchSignatureForms = useCallback(
    () => client.query({query: GET_ALL_SIGNATURE_FORMS_ACTIVE, fetchPolicy: 'network-only'}).then((res) => res.data),
    [client]
  )
  const signatureFormsCatalog = useStaticCatalog('signature-forms-active', enabled, fetchSignatureForms)

  const fetchCarriers = useCallback(
    () => client.query({query: GET_ALL_CARRIERS_LIST, fetchPolicy: 'network-only'}).then((res) => res.data),
    [client]
  )
  const carriersCatalog = useStaticCatalog('carriers-list', enabled, fetchCarriers)

  const fetchStates = useCallback(
    () => client.query({query: GET_ALL_STATES_EU_LIST, fetchPolicy: 'network-only'}).then((res) => res.data),
    [client]
  )
  const statesCatalog = useStaticCatalog('states-eu-list', enabled, fetchStates)

  const fetchPolicyStatuses = useCallback(
    () => client.query({query: GET_ALL_POLICIES_STATUSES, fetchPolicy: 'network-only'}).then((res) => res.data),
    [client]
  )
  const canViewAllPolicyStatuses = hasPermission(permissions, ALL_PERMISSIONS.VIEW_POLICY_STATUS)
  const policyStatusesCatalog = useStaticCatalog(
    `policy-statuses:${canViewAllPolicyStatuses ? 'all' : 'agents'}`,
    enabled,
    fetchPolicyStatuses
  )

  if (usersCatalog.error) toast.error(intl.formatMessage({id: 'contacts.page.toast.errorUsers', defaultMessage: 'Error loading users'}))
  if (signatureFormsCatalog.error) toast.error(intl.formatMessage({id: 'contacts.page.toast.errorSignatureForms', defaultMessage: 'Error loading signature forms'}))
  if (carriersCatalog.error) toast.error(intl.formatMessage({id: 'contacts.page.toast.errorCarriers', defaultMessage: 'Error loading carriers'}))
  if (statesCatalog.error) toast.error(intl.formatMessage({id: 'contacts.page.toast.errorStates', defaultMessage: 'Error loading states'}))
  if (policyStatusesCatalog.error) toast.error(intl.formatMessage({id: 'contacts.page.toast.errorPolicyStatuses', defaultMessage: 'Error loading policy statuses'}))

  return {
    dataUsers: usersCatalog.data,
    dataSignatureForms: signatureFormsCatalog.data,
    dataCarriers: carriersCatalog.data,
    dataPolicyStatuses: policyStatusesCatalog.data,
    dataStates: statesCatalog.data,
    loadingUsers: usersCatalog.loading,
    loadingSignatureForms: signatureFormsCatalog.loading,
    loadingCarriers: carriersCatalog.loading,
    loadingStates: statesCatalog.loading,
    loadingPolicyStatuses: policyStatusesCatalog.loading,
  }
}

export function useLocalStorage<T>(key: string, initialValue: T) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key)
      return item ? (JSON.parse(item) as T) : initialValue
    } catch {
      return initialValue
    }
  })

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(storedValue))
    } catch {
      // puede fallar en modo incógnito
    }
  }, [key, storedValue])

  return [storedValue, setStoredValue] as const
}



export const ContactsPage = () => {
  const intl = useIntl()
  const space = ' '
  const permissions = useGetUser((user) => user?.user?.permissions) ?? []
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const isSuperAdmin = userGroups.includes('SuperAdministrador')
  const currentUserId = useGetUser((s) => s.user?.user?.id)
  const canViewContacts = hasPermission(permissions, ALL_PERMISSIONS.VIEW_CONTACT)
  const canViewSignatureForms = hasPermission(permissions, ALL_PERMISSIONS.VIEW_SIGNATURE_FORMS)
  const canViewCommunications = hasPermission(permissions, ALL_PERMISSIONS.VIEW_COMMUNICATION_LOGS)
  const canMergeContacts = hasPermission(permissions, ALL_PERMISSIONS.EDIT_CONTACT)

  const selectMenuPortalTarget =
    typeof document !== 'undefined' ? document.body : undefined

  
  const [gqlGetContacts, {data: dataAllContacts, loading, error, refetch}] =
    useLazyQuery<IResContactsAPI>(GET_CONTACTS_PAGE_API, {
      fetchPolicy: 'no-cache',
    })

  const [gqlCreateP] = useMutation<IResCreateContactApi>(EDIT_CONTACT)
  const [gqlDeleteContact, {loading: loadingDeleteContact}] = useMutation(DELETE_CONTACT)
  const [gqlAnalyzeDuplicates, {loading: analyzingDuplicates}] = useMutation(
    ANALYZE_DUPLICATE_CONTACTS
  )
  const [contactFiltersQueried, setContactFiltersQueried] = useState(false)
  const {data: dataContactFilters, refetch: refetchContactFilters} = useQuery<{
    myContactFilters: {
      id: string
      name: string
      filters: string | ContactFilters
      created?: string
      updated?: string
    }[]
  }>(GET_MY_CONTACT_FILTERS, {
    fetchPolicy: 'network-only',
    skip: !contactFiltersQueried,
  })
  const [gqlCreateContactFilter] = useMutation(CREATE_CONTACT_FILTER)
  const [gqlUpdateContactFilter] = useMutation(UPDATE_CONTACT_FILTER)
  const [gqlDeleteContactFilter] = useMutation(DELETE_CONTACT_FILTER)

  const [filtersContacts] = useState<
    {id: string; name: string; icon: string}[]
  >([])

  const {isOpen: isOpenFilters, onOpen: onOpenFilters, onClose: onCloseFilters} = useDisclourse()

  const [filterCatalogsEnabled, setFilterCatalogsEnabled] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setFilterCatalogsEnabled(true), 1500)
    return () => clearTimeout(t)
  }, [])

  const {
    dataUsers,
    dataSignatureForms,
    dataCarriers,
    dataPolicyStatuses,
    dataStates,
  } = useLoadFilterData(filterCatalogsEnabled)

  const filterData = {
    users: dataUsers?.allUsersList?.map((u) => ({
      ...u,
      fullName: `${u.firstName} ${u.lastName}`,
    })) || [],
    states: dataStates?.allStatesList || [],
    immigrationStatuses: IMMIGRATION_STATUS_CHOICES,
    signatureForms: dataSignatureForms?.allSignatureForms?.edges.map(({node}) => node) || [],
    carriers: dataCarriers?.allInsuranceOperatorsList || [],
    policyStatuses:
      dataPolicyStatuses?.allPolicyStatuses?.edges.map(({node}) => ({
        id: node.id,
        status: node.code,
      })) || [],
  }

  const {data: tagsData} = useQuery(GET_CONTACT_TAGS, {skip: !filterCatalogsEnabled})
  const tagOptions = (tagsData?.contactTags || []).map((t: any) => ({value: t.id, label: t.name}))

  
  const [, setShowAside] = useState(false)
  const [showTagManager, setShowTagManager] = useState(false)
  const [tagTargetId, setTagTargetId] = useState<string | null>(null)
  const {isOpen, onClose, onOpen} = useDisclourse()
  const {isOpen: isOpenPreview, onClose: onClosePreview, onOpen: onOpenPreview} = useDisclourse()
  const {isOpen: isOpenRequest, onClose: onCloseRequest, onOpen: onOpenRequest} = useDisclourse()
  const {isOpen: isOpenQuote, onClose: onCloseQuote, onOpen: onOpenQuote} = useDisclourse()
  const {isOpen: isOpenAssignContact, onClose: onCloseAssignContact, onOpen: onOpenAssignContact} = useDisclourse()
  const {
    isOpen: isOpenCommunications,
    onOpen: onOpenCommunications,
    onClose: onCloseCommunications,
  } = useDisclourse()
  const {
    isOpen: isOpenMessaging,
    onOpen: onOpenMessaging,
    onClose: onCloseMessaging,
  } = useDisclourse()


  const [tabFilters, setTabFilters] = useState('Contacts')
  const [contactToDelete, setContactToDelete] = useState<NodeContact | null>(null)
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([])
  const [mergeGroups, setMergeGroups] = useState<MergeContactCandidate[][]>([])
  const [mergeAnalysis, setMergeAnalysis] = useState<{
    analyzedCount?: number
    truncated?: boolean
  }>({})
  const [showMergeContacts, setShowMergeContacts] = useState(false)

  const [infoContact, setInfoContact] = useState<{hasEmail: boolean; hasPhone: boolean}>()
  const [idContact, setIdContact] = useState<string>()
  const [infoPreview, setInfoPreview] = useState<null | NodeContact>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [first, setFirst] = useState(SIZE_PAGE_DEFAULT)

  const [isPag] = useState(false)
  const [page, setPage] = useState(1)

  const [dataArr, setDataArr] = useState<IResContactsAPI['allContacts']['edges']>([])

  let totalContacts = dataAllContacts?.allContacts?.totalCount ?? 0

  const totalPages = Math.ceil(totalContacts / first)
  const {start, end} = PaginationUtils.getPaginationRange(currentPage, first, totalContacts)

  const [formData, setFormData] = useLocalStorage<ContactFilters>(
    'contacts-filters',
    {...INITIAL_CONTACT_FILTERS}
  )
  const parseContactFilterPayload = (rawFilters: string | ContactFilters | undefined) => {
    if (!rawFilters) return INITIAL_CONTACT_FILTERS
    if (typeof rawFilters === 'string') {
      try {
        const parsed = JSON.parse(rawFilters) as ContactFilters
        return {...INITIAL_CONTACT_FILTERS, ...parsed}
      } catch {
        return INITIAL_CONTACT_FILTERS
      }
    }
    return {...INITIAL_CONTACT_FILTERS, ...rawFilters}
  }

  const savedContactFilters: ContactSavedFilter[] =
    dataContactFilters?.myContactFilters?.map((filter) => ({
      id: filter.id,
      name: filter.name,
      created: filter.created,
      updated: filter.updated,
      filters: parseContactFilterPayload(filter.filters),
    })) || []

  const {
    data: dataOrganizations,
    loading: loadingOrganizations,
    error: errorOrganizations,
  } = useQuery(GET_ALL_ORGANIZATIONS, {
    variables: {first: 100},
    fetchPolicy: 'no-cache',
    skip: !isSuperAdmin,
  })

  const handleSelectChange = (field: string, selected: any) => {
    let value = selected?.value
    if (value === 'true') value = true
    else if (value === 'false') value = false
    else if (value === '') value = undefined

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }



  const cleanVariables = (vars: any) => {
    return Object.fromEntries(
      Object.entries(vars).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
    )
  }

  const serializeImmigrationStatus = (value: ContactFilters['immigrationStatus']) => {
    if (Array.isArray(value)) {
      const normalized = value.map((item) => String(item).trim()).filter(Boolean)
      return normalized.length ? normalized.join(',') : undefined
    }

    if (typeof value === 'string') {
      const normalized = value.trim()
      return normalized || undefined
    }

    return undefined
  }

  const handleContactFilterErrors = (
    errors: {field?: string; message?: string}[] | undefined,
    fallback: string
  ) => {
    const message = errors?.[0]?.message || fallback
    toast.error(message)
  }

  const handleSaveContactFilter = async (name: string, filters: ContactFilters) => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      toast.error(intl.formatMessage({id: 'contacts.page.toast.filterNameRequired', defaultMessage: 'Filter name is required.'}))
      return null
    }

    const result = await gqlCreateContactFilter({
      variables: {
        contactFilterData: {
          name: trimmedName,
          filters: JSON.stringify(filters),
        },
      },
    })

    const payload = result.data?.createContactFilter
    if (!payload?.success) {
      handleContactFilterErrors(payload?.errors, intl.formatMessage({id: 'contacts.page.toast.unableSaveFilter', defaultMessage: 'Unable to save filter.'}))
      return null
    }

    await refetchContactFilters()
    toast.success(intl.formatMessage({id: 'contacts.page.toast.filterSaved', defaultMessage: 'Filter saved.'}))

    if (!payload.contactFilter) return null
    return {
      id: payload.contactFilter.id,
      name: payload.contactFilter.name,
      filters: parseContactFilterPayload(payload.contactFilter.filters),
      updated: payload.contactFilter.updated,
    } as ContactSavedFilter
  }

  const handleUpdateContactFilter = async (
    id: string,
    name: string,
    filters: ContactFilters
  ) => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      toast.error(intl.formatMessage({id: 'contacts.page.toast.filterNameRequired', defaultMessage: 'Filter name is required.'}))
      return null
    }

    const result = await gqlUpdateContactFilter({
      variables: {
        id: parseId(id),
        contactFilterData: {
          name: trimmedName,
          filters: JSON.stringify(filters),
        },
      },
    })

    const payload = result.data?.updateContactFilter
    if (!payload?.success) {
      handleContactFilterErrors(payload?.errors, intl.formatMessage({id: 'contacts.page.toast.unableUpdateFilter', defaultMessage: 'Unable to update filter.'}))
      return null
    }

    await refetchContactFilters()
    toast.success(intl.formatMessage({id: 'contacts.page.toast.filterUpdated', defaultMessage: 'Filter updated.'}))

    if (!payload.contactFilter) return null
    return {
      id: payload.contactFilter.id,
      name: payload.contactFilter.name,
      filters: parseContactFilterPayload(payload.contactFilter.filters),
      updated: payload.contactFilter.updated,
    } as ContactSavedFilter
  }

  const handleDeleteContactFilter = async (id: string) => {
    const result = await gqlDeleteContactFilter({
      variables: {id: parseId(id)},
    })

    const payload = result.data?.deleteContactFilter
    if (!payload?.success) {
      handleContactFilterErrors(payload?.errors, intl.formatMessage({id: 'contacts.page.toast.unableDeleteFilter', defaultMessage: 'Unable to delete filter.'}))
      return false
    }

    await refetchContactFilters()
    toast.success(intl.formatMessage({id: 'contacts.page.toast.filterDeleted', defaultMessage: 'Filter deleted.'}))
    return true
  }

  const handleFilters = () => {
    const filtersToSend: any = { ...formData }

    // ❌ No enviar si está en false
    if (filtersToSend.isPolicyHolder === false) {
      delete filtersToSend.isPolicyHolder
    }

    // hasSignedEntry queda "pegado" en formData si signatureStatus se vuelve a Select...
    if (!filtersToSend.signatureStatus) {
      delete filtersToSend.hasSignedEntry
    }

    const variables = cleanVariables({
      ...filtersToSend,
      immigrationStatus: serializeImmigrationStatus(filtersToSend.immigrationStatus),
      isImportant: tabFilters === 'Important' ? true : formData.isImportant,
      withoutLead: tabFilters === 'Leads' ? false : formData.withoutLead,
      user: formData.user ? parseId(formData.user) : null,
      assignedTo: formData.assignedTo ? parseId(formData.assignedTo) : null,
      organization: isSuperAdmin && formData.organizationId ? parseId(formData.organizationId) : null,
      first,
      offset: (currentPage - 1) * first,
    })

    gqlGetContacts({ variables })
  }


  const handleFilterApply = (filters: any) => {
    const transformedFilters = {...filters}

    if (filters.signatureStatus === 'SIGNED') {
      transformedFilters.hasSignedEntry = true
    } else if (filters.signatureStatus === 'NOT_SIGNED') {
      transformedFilters.hasSignedEntry = false
    } else {
      transformedFilters.hasSignedEntry = undefined
    }

    delete transformedFilters.signatureStatus

    const newFormData = {
      ...formData,
      ...transformedFilters,
    }

    // ❌ No enviar si está en false
    if (newFormData.isPolicyHolder === false) {
      delete newFormData.isPolicyHolder
    }

    setFormData(newFormData)

    const variables = cleanVariables({
      ...newFormData,
      immigrationStatus: serializeImmigrationStatus(newFormData.immigrationStatus),
      isImportant: tabFilters === 'Important' ? true : newFormData.isImportant,
      withoutLead: tabFilters === 'Leads' ? false : newFormData.withoutLead,
      user: newFormData.user ? parseId(newFormData.user) : null,
      assignedTo: newFormData.assignedTo ? parseId(newFormData.assignedTo) : null,
      organization: isSuperAdmin && newFormData.organizationId ? parseId(newFormData.organizationId) : null,
      first,
      offset: (currentPage - 1) * first,
    })
    gqlGetContacts({ variables })
  }

  const buildContactFiltersVariables = (nextFormData: ContactFilters, page: number) => {
    const filtersToSend: any = {...nextFormData}
    if (filtersToSend.isPolicyHolder === false) {
      delete filtersToSend.isPolicyHolder
    }
    if (!filtersToSend.signatureStatus) {
      delete filtersToSend.hasSignedEntry
    }

    return cleanVariables({
      ...filtersToSend,
      immigrationStatus: serializeImmigrationStatus(nextFormData.immigrationStatus),
      isImportant: tabFilters === 'Important' ? true : nextFormData.isImportant,
      withoutLead: tabFilters === 'Leads' ? false : nextFormData.withoutLead,
      user: nextFormData.user ? parseId(nextFormData.user) : null,
      assignedTo: nextFormData.assignedTo ? parseId(nextFormData.assignedTo) : null,
      organization: isSuperAdmin && nextFormData.organizationId ? parseId(nextFormData.organizationId) : null,
      first,
      offset: (page - 1) * first,
    })
  }

  const clearFilters = () => {
    const next = {...INITIAL_CONTACT_FILTERS}
    setFormData(next)
    setCurrentPage(1)
    gqlGetContacts({variables: buildContactFiltersVariables(next, 1)})
  }

  const handleRemoveFilter = (filterKey: string | string[]) => {
    const next: any = {...formData}
    const keysToReset = Array.isArray(filterKey) ? filterKey : [filterKey]

    keysToReset.forEach((key) => {
      if (Array.isArray(next[key])) next[key] = []
      else if (typeof next[key] === 'boolean') next[key] = undefined
      else next[key] = ''
    })

    setFormData(next)
    setCurrentPage(1)
    gqlGetContacts({variables: buildContactFiltersVariables(next, 1)})
  }

  const sidebarSelectedTagIds = formData.tags ? formData.tags.split(',').filter(Boolean) : []

  const handleSidebarTagClick = (tagId: string) => {
    const nextTagIds = sidebarSelectedTagIds.includes(tagId)
      ? sidebarSelectedTagIds.filter((id) => id !== tagId)
      : [...sidebarSelectedTagIds, tagId]
    const next = {...formData, tags: nextTagIds.join(',')}
    setFormData(next)
    setCurrentPage(1)
    gqlGetContacts({variables: buildContactFiltersVariables(next, 1)})
  }

  const monthOptions = [
    {id: 'common.month.january', defaultMessage: 'January'},
    {id: 'common.month.february', defaultMessage: 'February'},
    {id: 'common.month.march', defaultMessage: 'March'},
    {id: 'common.month.april', defaultMessage: 'April'},
    {id: 'common.month.may', defaultMessage: 'May'},
    {id: 'common.month.june', defaultMessage: 'June'},
    {id: 'common.month.july', defaultMessage: 'July'},
    {id: 'common.month.august', defaultMessage: 'August'},
    {id: 'common.month.september', defaultMessage: 'September'},
    {id: 'common.month.october', defaultMessage: 'October'},
    {id: 'common.month.november', defaultMessage: 'November'},
    {id: 'common.month.december', defaultMessage: 'December'},
  ].map((month, index) => ({value: String(index + 1), label: intl.formatMessage(month)}))

  const contactUserOptions = filterData.users.map((u: any) => ({value: u.id, label: `${u.fullName} - ${u.email}`}))
  const languageOptions = [
    {value: 'Spanish', label: intl.formatMessage({id: 'common.language.spanish', defaultMessage: 'Spanish'})},
    {value: 'English', label: intl.formatMessage({id: 'common.language.english', defaultMessage: 'English'})},
  ]
  const genderLabelIds: Record<string, {id: string; defaultMessage: string}> = {
    Male: {id: 'common.gender.male', defaultMessage: 'Male'},
    Female: {id: 'common.gender.female', defaultMessage: 'Female'},
    Other: {id: 'common.option.other', defaultMessage: 'Other'},
  }
  const genderOptions = GENDER_OPTIONS.map((s) => ({
    value: s.value,
    label: genderLabelIds[s.value] ? intl.formatMessage(genderLabelIds[s.value]) : s.label,
  }))
  const contactStateOptions = filterData.states.map((s: any) => ({value: s.code, label: s.name}))
  const signatureStatusOptions = [
    {value: 'SIGNED', label: intl.formatMessage({id: 'common.status.signed', defaultMessage: 'Signed'})},
    {value: 'NOT_SIGNED', label: intl.formatMessage({id: 'contacts.filters.status.notSigned', defaultMessage: 'Did not sign'})},
  ]
  const contactSignatureFormOptions = filterData.signatureForms.map((f: any) => ({value: f.id, label: f.name}))
  const contactCarrierOptions = filterData.carriers.map((c: any) => ({value: c.id, label: c.name}))
  const contactPolicyStatusOptions = filterData.policyStatuses.map((s: any) => ({value: s.status, label: s.status}))

  const formatDate = (dateStr: string) => {
    if (!dateStr) return ''
    const [year, month, day] = dateStr.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    return date.toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'})
  }

  const contactFiltersConfig = [
    {key: 'user', label: intl.formatMessage({id: 'common.field.user', defaultMessage: 'User'}), options: contactUserOptions},
    {key: 'preferredLanguage', label: intl.formatMessage({id: 'contacts.profile.field.preferredLanguage', defaultMessage: 'Preferred Language'}), options: languageOptions},
    {key: 'birthMonth', label: intl.formatMessage({id: 'contacts.filters.field.birthMonth', defaultMessage: 'Date of Birth Month'}), options: monthOptions},
    {key: 'ageFrom', label: intl.formatMessage({id: 'contacts.filters.field.ageFrom', defaultMessage: 'Age from'})},
    {key: 'ageTo', label: intl.formatMessage({id: 'contacts.filters.field.ageTo', defaultMessage: 'Age to'})},
    {key: 'gender', label: intl.formatMessage({id: 'common.field.gender', defaultMessage: 'Gender'}), options: genderOptions},
    {key: 'state', label: intl.formatMessage({id: 'common.field.state', defaultMessage: 'State'}), options: contactStateOptions},
    {key: 'zipCode', label: intl.formatMessage({id: 'common.field.zipCode', defaultMessage: 'Zip Code'})},
    {key: 'immigrationStatus', label: intl.formatMessage({id: 'contacts.profile.field.immigrationStatus', defaultMessage: 'Immigration Status'}), options: filterData.immigrationStatuses},
    {
      key: 'tags',
      label: intl.formatMessage({id: 'contacts.filters.field.tags', defaultMessage: 'Tags'}),
      transform: (value: string) => {
        const ids = value.split(',').filter(Boolean)
        const labels = ids
          .map((id) => tagOptions.find((o: any) => String(o.value) === id)?.label)
          .filter(Boolean)
        return labels.length ? labels.join(', ') : value
      },
    },
    {key: 'signatureStatus', label: intl.formatMessage({id: 'contacts.filters.field.signatureStatus', defaultMessage: 'Signature Form Status'}), options: signatureStatusOptions},
    {key: 'signatureForm', label: intl.formatMessage({id: 'contacts.filters.field.signatureForm', defaultMessage: 'Signature Form'}), options: contactSignatureFormOptions},
    {key: 'carrier', label: intl.formatMessage({id: 'contacts.filters.field.carrier', defaultMessage: 'Carrier'}), options: contactCarrierOptions},
    {key: 'policyStatus', label: intl.formatMessage({id: 'contacts.filters.field.policyStatus', defaultMessage: 'Policy Status'}), options: contactPolicyStatusOptions},
  ]

  const contactDateRangeFilters = [
    {keyFrom: 'createdAfter', keyTo: 'createdBefore', label: intl.formatMessage({id: 'contacts.page.dateRange.contactCreated', defaultMessage: 'Contact Created'})},
    {keyFrom: 'effectiveDateAfter', keyTo: 'effectiveDateBefore', label: intl.formatMessage({id: 'contacts.page.dateRange.effectiveDate', defaultMessage: 'Effective Date'})},
    {keyFrom: 'policyCreatedFrom', keyTo: 'policyCreatedTo', label: intl.formatMessage({id: 'contacts.page.dateRange.policyCreated', defaultMessage: 'Policy Created Date'})},
    {keyFrom: 'submissionDateFrom', keyTo: 'submissionDateTo', label: intl.formatMessage({id: 'contacts.page.dateRange.submissionDate', defaultMessage: 'Submission Date'})},
  ]

  useEffect(() => {
    handleFilters()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first, gqlGetContacts, currentPage, tabFilters])

  useEffect(() => {
    if (isOpenFilters && !contactFiltersQueried) {
      setContactFiltersQueried(true)
    }
  }, [isOpenFilters, contactFiltersQueried])

  useEffect(() => {
    if (errorOrganizations) {
      toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: errorOrganizations.message}))
    }
  }, [errorOrganizations])

  const handleSearch = () => {
    setCurrentPage(1)
    gqlGetContacts({variables: buildContactFiltersVariables(formData, 1)})
  }

  const handleContactName = () => {
    handleSearch()
  }

  useEffect(() => {
    if (!dataAllContacts) return
    const {edges} = dataAllContacts.allContacts
    // if (edges.length === 0) return
    if (isPag) {
      setDataArr((p) => [...p, ...edges])
    } else {
      setDataArr(edges)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAllContacts])

  useEffect(() => {
    if (error) {
      toast.error(intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message: error.message}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const updatePage = (n: number) => {
    setCurrentPage(n)
  }

  const handleUpdateContact = async (id: string, data: any) => {
    gqlCreateP({
      variables: {
        id: parseId(id),
        contactData: data,
      },
    })
  }

  const handleDeleteContact = async (id: string) => {
    const result = await gqlDeleteContact({
      variables: {id: parseId(id)},
    })

    const payload = result.data?.deleteContact
    if (!payload?.success) {
      toast.error(payload?.errors?.[0]?.message || intl.formatMessage({id: 'contacts.page.toast.unableDeleteContact', defaultMessage: 'Unable to delete contact.'}))
      return
    }

    toast.success(intl.formatMessage({id: 'contacts.page.toast.contactDeleted', defaultMessage: 'Contact deleted.'}))
    setContactToDelete(null)
    await handleFilters()
  }

  const currentPageContacts = dataArr.slice(first * page - first, first * page)
  const currentPageIds = currentPageContacts.map(({node}) => node.id)
  const allCurrentPageSelected =
    currentPageIds.length > 0 && currentPageIds.every((id) => selectedContactIds.includes(id))

  const toggleContactSelection = (id: string) => {
    setSelectedContactIds((current) =>
      current.includes(id) ? current.filter((contactId) => contactId !== id) : [...current, id]
    )
  }

  const toggleCurrentPage = () => {
    setSelectedContactIds((current) =>
      allCurrentPageSelected
        ? current.filter((id) => !currentPageIds.includes(id))
        : Array.from(new Set([...current, ...currentPageIds]))
    )
  }

  const openManualMerge = () => {
    const contacts = dataArr
      .map(({node}) => node)
      .filter((node) => selectedContactIds.includes(node.id))
      .map((node) => {
        const memberIds = new Set((node.members?.edges || []).map((edge) => parseId(edge.node.id)))
        if (node.memberPolicy?.id) memberIds.add(parseId(node.memberPolicy.id))
        return {
          id: Number(parseId(node.id)),
          fullName: node.fullName,
          email: node.email,
          phone: node.phone,
          dateBirth: node.dateBirth,
          policyCount: memberIds.size,
          created: node.created,
          reasons: ['Manually selected'],
        }
      })
    if (contacts.length < 2) {
      toast.error('Select at least two contacts from the table.')
      return
    }
    setMergeGroups([contacts])
    setMergeAnalysis({})
    setShowMergeContacts(true)
  }

  const analyzeFilteredContacts = async () => {
    try {
      const filters: any = {
        ...formData,
        immigrationStatus: serializeImmigrationStatus(formData.immigrationStatus),
        isImportant: tabFilters === 'Important' ? true : formData.isImportant,
        withoutLead: tabFilters === 'Leads' ? false : formData.withoutLead,
        user: formData.user ? parseId(formData.user) : undefined,
        assignedTo: formData.assignedTo ? parseId(formData.assignedTo) : undefined,
        organization:
          isSuperAdmin && formData.organizationId ? parseId(formData.organizationId) : undefined,
      }
      delete filters.organizationId
      delete filters.signatureStatus
      // This legacy Medicare default is not part of the Contacts table query.
      // Sending it only to duplicate analysis made its result differ from the table.
      delete filters.medicareUnifiedFilter
      if (filters.isPolicyHolder === false) delete filters.isPolicyHolder
      Object.keys(filters).forEach((key) => {
        if (filters[key] === '' || filters[key] === null || filters[key] === undefined)
          delete filters[key]
      })

      const result = await gqlAnalyzeDuplicates({variables: {filters}})
      const payload = result.data?.analyzeDuplicateContacts
      if (!payload?.success)
        throw new Error(payload?.errors?.[0]?.message || 'Unable to analyze duplicate contacts.')
      setMergeGroups(payload.groups.map((group: any) => group.contacts))
      setMergeAnalysis({
        analyzedCount: payload.analyzedCount,
        truncated: payload.truncated,
      })
      setShowMergeContacts(true)
    } catch (error: any) {
      toast.error(error.message || 'Unable to analyze duplicate contacts.')
    }
  }

  // if ((loading || !dataAllContacts) && !isPag) {
  //   return <LoadingContacts />
  // }

  if (!canViewContacts) {
    return (
      <div className='policies-page'>
        <header className='header'>
          <Breadcrumb>
            <Breadcrumb.Item className='fs-3'>
              <FormattedMessage id='contacts.page.breadcrumb' defaultMessage='Contacts' />
            </Breadcrumb.Item>
          </Breadcrumb>
        </header>
        <div className='d-flex flex-center' style={{height: '40vh'}}>
          <h4>
            <FormattedMessage
              id='contacts.page.noPermission'
              defaultMessage="You don't have permission to view Contacts."
            />
          </h4>
        </div>
      </div>
    )
  }

  return (
    <div className='policies-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item className='d-blok d-md-none' onClick={() => setShowAside(true)}>
            <span>
              <i className='bi bi-list fs-1' />
            </span>
          </Breadcrumb.Item>
          <Breadcrumb.Item className='fs-3'>
            <FormattedMessage id='contacts.page.breadcrumb' defaultMessage='Contacts' />
          </Breadcrumb.Item>
          {/* <Breadcrumb.Item active>Quote </Breadcrumb.Item> */}
        </Breadcrumb>

        <div className='d-flex align-items-center gap-2'>
          {canMergeContacts && (
            <>
              <Button
                variant='outline-primary'
                size='sm'
                onClick={() => void analyzeFilteredContacts()}
                disabled={analyzingDuplicates}
              >
                {analyzingDuplicates ? (
                  <Spinner animation='border' size='sm' className='me-2' />
                ) : (
                  <i className='bi bi-search me-2' />
                )}
                Find duplicates
              </Button>
              <Button
                variant='outline-primary'
                size='sm'
                onClick={openManualMerge}
                disabled={selectedContactIds.length < 2}
              >
                <i className='bi bi-intersect me-2' />
                Merge selected ({selectedContactIds.length})
              </Button>
            </>
          )}
          <Button variant='outline-secondary' size='sm' onClick={() => setShowTagManager(true)}>
            <i className='bi bi-tags me-2' />
            <FormattedMessage id='contacts.page.manageTags' defaultMessage='Manage Tags' />
          </Button>
          <Button
            size='sm'
            onClick={() => {
              setIdContact('')
              onOpen()
            }}
          >
            <i className='bi bi-plus fs-3' /> <FormattedMessage id='contacts.page.newContact' defaultMessage='New Contact' />
          </Button>
        </div>
      </header>
      <Row className='policies'>
        <Col md={3} lg={3} sm={0} xl={3} className='menu'>
          <div className='menu-aside'>
            <div className='title'>
              <h5 style={{fontSize: '13px'}}>
                <FormattedMessage id='contacts.page.menu.title' defaultMessage='Menu' />
              </h5>
            </div>
            <div className='list-menu'>
              <ListGroup variant='flush'>
                {/* All Contacts */}
                <ListGroup.Item
                  action
                  variant={tabFilters === 'AllContacts' ? 'primary' : 'light'}
                  onClick={() => {
                    setTabFilters('AllContacts')
                    setFormData(INITIAL_CONTACT_FILTERS) // sin filtros especiales
                  }}
                >
                  <i className='bi bi-person-fill fs-3' style={{color: 'inherit'}} /> <FormattedMessage id='contacts.page.menu.allContacts' defaultMessage='All Contacts' />
                </ListGroup.Item>

                {/* Leads */}
                <ListGroup.Item
                  action
                  variant={tabFilters === 'Leads' ? 'primary' : 'light'}
                  onClick={() => {
                    setTabFilters('Leads')
                    setFormData((prev) => ({
                      ...INITIAL_CONTACT_FILTERS,
                      withoutLead: false, // solo con lead asociado
                    }))
                  }}
                >
                  <i className='bi bi-currency-dollar fs-3' style={{color: 'inherit'}} /> <FormattedMessage id='contacts.page.menu.leads' defaultMessage='Leads' />
                </ListGroup.Item>

                {/* Favoritos guardados dinámicos */}
                {filtersContacts.length > 0 &&
                  filtersContacts.map((lis) => (
                    <ListGroup.Item
                      key={lis.name}
                      className='filters-favorites'
                      action
                      variant={tabFilters === lis.id ? 'primary' : 'light'}
                      onClick={() => {
                        setTabFilters(lis.id)
                        setFormData((prev) => ({...prev, insuranceCategory: lis.id}))
                      }}
                    >
                      {lis.icon && (
                        <i
                          className={`bi bi-${lis.icon} fs-3`}
                          style={{color: 'inherit', marginRight: '8px'}}
                        />
                      )}
                      {lis.name}
                    </ListGroup.Item>
                  ))}

                {/* Important */}
                <ListGroup.Item
                  action
                  variant={tabFilters === 'Important' ? 'primary' : 'light'}
                  onClick={() => setTabFilters('Important')}
                >
                  <i className='bi bi-star-fill fs-3' style={{color: 'inherit'}} /> <FormattedMessage id='contacts.page.menu.important' defaultMessage='Important' />
                </ListGroup.Item>
              </ListGroup>
            </div>

            <div className='list-menu mt-4'>
              <h6 className='px-2' style={{fontSize: '13px', fontWeight: 700}}>
                <FormattedMessage id='contacts.filters.field.tags' defaultMessage='Tags' />
              </h6>
              {tagOptions.length === 0 ? (
                <p className='text-muted small px-2'>
                  <i className='bi bi-tags me-1' />
                  <FormattedMessage id='contacts.page.noTagsYet' defaultMessage='No tags yet' />
                </p>
              ) : (
                <ListGroup variant='flush'>
                  {(tagsData?.contactTags || []).map((tag: any) => {
                    const isActive = sidebarSelectedTagIds.includes(tag.id)
                    return (
                      <ListGroup.Item
                        key={tag.id}
                        action
                        active={isActive}
                        onClick={() => handleSidebarTagClick(tag.id)}
                        className='d-flex justify-content-between align-items-center py-1'
                      >
                        <span
                          className='badge rounded-pill'
                          style={{
                            backgroundColor: tag.color,
                            color: getTagContrastColor(tag.color),
                            fontSize: '0.75rem',
                          }}
                        >
                          {tag.name}
                        </span>
                        <Badge bg='secondary' className='ms-2'>
                          {tag.contactCount || 0}
                        </Badge>
                      </ListGroup.Item>
                    )
                  })}
                </ListGroup>
              )}
            </div>

            {/* <div
              style={{
                borderBottom: '2px solid rgba(93, 120, 255, 0.2)',
              }}
              className='d-flex align-items-center justify-content-between'
            >
              <h5 className='m-0 p-0'>Agency Folders</h5>
              <Button variant='link'>
                <i className='bi bi-pencil-square' style={{color: 'inherit'}} /> Manage
              </Button>
            </div>
            <p className='my-4'>Your agency has no folders.</p>
            <div
              style={{
                borderBottom: '2px solid rgba(93, 120, 255, 0.2)',
              }}
              className='d-flex align-items-center justify-content-between mt-5'
            >
              <h5 className='m-0 p-0'>
                Agency Folders
                <span>
                  <span data-tooltip-id='asdf'>
                    <i className='bi bi-info-circle-fill fs-5 ms-1' style={{color: '#5867dd'}} />
                  </span>
                  <Tooltip
                    id='asdf'
                    place='right'
                    style={{
                      backgroundColor: 'white',
                      color: 'black',
                      boxShadow: '2px 2px 2px 1px rgba(0, 0, 0, 0.2)',
                      opacity: 1,
                    }}
                  >
                    <div className='tooltip-tfi'>
                      <h6 className='text-center'>English</h6>
                      <p className='text-muted'>
                        Only policies created by you can be stored in your personal folders.
                      </p>
                      <h6 className='text-center'>Español</h6>
                      <p className='text-muted'>
                        Una dirección de facturación es la dirección relacionada con una forma de
                        pago específica.
                      </p>
                    </div>
                  </Tooltip>
                </span>
              </h5>
              <Button variant='link'>
                <i className='bi bi-pencil-square' style={{color: 'inherit'}} /> Manage
              </Button>
            </div>
            <p className='my-4'>You have no personal folders.</p> */}
          </div>
        </Col>
        <Col md={8} lg={8} sm={12} xl={9} className='container'>
          <div className='policies-container'>
            <div className='title-header'>
              <h3>
                <FormattedMessage id='contacts.page.menu.allContacts' defaultMessage='All Contacts' />
              </h3>
            </div>
            <div className='filters-header'>
              <InputGroup>
                <Form.Control
                  placeholder={intl.formatMessage({id: 'contacts.page.searchPlaceholder', defaultMessage: 'Type your query here...'})}
                  aria-label={intl.formatMessage({id: 'contacts.page.searchAriaLabel', defaultMessage: "Contact's username with two button addons"})}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault() // evita el submit automático
                      handleContactName() // si tienes una función como esa
                    }
                  }}
                  value={formData.name}
                />
                {isSuperAdmin && (
                  <Form.Select
                    value={formData.organizationId}
                    onChange={(e) => {
                      const value = e.target.value
                      setFormData((prev) => ({...prev, organizationId: value}))
                      setCurrentPage(1)
                    }}
                    disabled={loadingOrganizations}
                  >
                    {loadingOrganizations ? (
                      <option value=''>{intl.formatMessage({id: 'common.loading', defaultMessage: 'Loading...'})}</option>
                    ) : (
                      <option value=''>{intl.formatMessage({id: 'contacts.page.allOrganizations', defaultMessage: 'All Organizations'})}</option>
                    )}
                    {dataOrganizations?.allOrganizations?.edges?.map(({node}: any) => (
                      <option key={node.id} value={parseId(node.id)}>
                        {node.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
                <Button
                  onClick={handleSearch}
                  variant='outline-success'
                  style={{border: '1px solid #dbdfe9'}}
                >
                  <FormattedMessage id='common.action.search' defaultMessage='Search' />
                </Button>
                <Button
                  variant='outline-primary'
                  style={{border: '1px solid #dbdfe9'}}
                  onClick={onOpenFilters}
                >
                  <FormattedMessage id='common.action.filter' defaultMessage='Filter' />
                </Button>
              </InputGroup>
              <div className='d-flex align-items-center gap-4 flex-wrap'>
                <Form.Check
                  type='checkbox'
                  id='policyholders-only'
                  checked={formData.isPolicyHolder ?? false}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isPolicyHolder: e.target.checked,
                    }))
                  }
                  label={
                    <span>
                      <FormattedMessage id='contacts.page.showPolicyholdersOnly' defaultMessage='Show policyholders only' />{space}
                      <span className='ms-2' data-tooltip-id='tooltip-isPolicyHolder'>
                        <i className='bi bi-question-circle-fill text-primary' />
                      </span>
                      <Tooltip
                        id='tooltip-isPolicyHolder'
                        place='bottom'
                        style={{
                          zIndex: 99999,
                          backgroundColor: 'white',
                          color: 'black',
                          boxShadow:
                            'rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px',
                        }}
                      >
                        <div className='tooltip-tfi'>
                          <p style={{color: '#222'}}>
                            <FormattedMessage
                              id='contacts.page.policyholdersTooltip'
                              defaultMessage='Check this box if you want to show only contacts who are policyholders.'
                            />
                          </p>
                        </div>
                      </Tooltip>
                    </span>
                  }
                />

                <Form.Label>
                  <FormattedMessage id='contacts.page.hasPolicy' defaultMessage='Has Policy' />
                </Form.Label>
                <Select
                  menuPortalTarget={selectMenuPortalTarget}
                  menuPosition='fixed'
                  styles={{
                    menuPortal: (base) => ({...base, zIndex: 9999}),
                  }}
                  value={
                    formData.withoutPolicy === true
                      ? { value: 'true', label: intl.formatMessage({id: 'common.boolean.no', defaultMessage: 'No'}) }
                      : formData.withoutPolicy === false
                      ? { value: 'false', label: intl.formatMessage({id: 'common.boolean.yes', defaultMessage: 'Yes'}) }
                      : { value: '', label: intl.formatMessage({id: 'common.pagination.all', defaultMessage: 'All'}) }
                  }
                  options={[
                    { value: '', label: intl.formatMessage({id: 'common.pagination.all', defaultMessage: 'All'}) },
                    { value: 'false', label: intl.formatMessage({id: 'common.boolean.yes', defaultMessage: 'Yes'}) },
                    { value: 'true', label: intl.formatMessage({id: 'common.boolean.no', defaultMessage: 'No'}) },
                  ]}
                  onChange={(selected) => handleSelectChange('withoutPolicy', selected)}
                />

                

                <Form.Check
                  type='checkbox'
                  id='my-contacts'
                  checked={formData.myContacts ?? false}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      myContacts: e.target.checked,
                    }))
                  }
                  label={
                    <span>
                      <FormattedMessage id='contacts.page.showMyContactsOnly' defaultMessage='Show my contacts only' />{space}
                      <span className='ms-2' data-tooltip-id='tooltip-myContacts'>
                        <i className='bi bi-question-circle-fill text-primary' />
                      </span>
                      <Tooltip
                        id='tooltip-myContacts'
                        place='bottom'
                        style={{
                          zIndex: 99999,
                          backgroundColor: 'white',
                          color: 'black',
                          boxShadow:
                            'rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px',
                        }}
                      >
                        <div className='tooltip-tfi'>
                          <p style={{color: '#222'}}>
                            <FormattedMessage
                              id='contacts.page.myContactsTooltip'
                              defaultMessage='Show only the contacts created by you.'
                            />
                          </p>
                        </div>
                      </Tooltip>
                    </span>
                  }
                />
              </div>
              <AppliedFiltersList
                filters={formData}
                config={contactFiltersConfig}
                dateRangeFilters={contactDateRangeFilters}
                formatDate={formatDate}
                clearFilters={clearFilters}
                onRemoveFilter={handleRemoveFilter}
              />
            </div>
            <div className='results-header'>
              <PaginationSection
                first={first}
                setFirst={(n) => {
                  setFirst(n)
                  setPage(1)
                }}
                currentPage={currentPage}
                totalPages={totalPages}
                start={start}
                end={end}
                totalPolicies={totalContacts}
                updatePage={updatePage}
                PaginationUtils={PaginationUtils}
              />
              <div className='col-md-3'>
                <div className='results-filter'>
                  <Col lg={12} md={12} sm={12} xs={12}></Col>
                </div>
              </div>
            </div>

            <div className='table-responsive table table-striped table-hover '>
              <table
                className='table-scrollable table-scroll  table table-striped table-hover '
                style={{whiteSpace: 'nowrap'}}
              >
                <thead>
                  <tr role='row'>
                    <th>
                      {canMergeContacts && <input
                        className='form-check-input'
                        type='checkbox'
                        aria-label='Select all contacts on this page'
                        checked={allCurrentPageSelected}
                        onChange={toggleCurrentPage}
                      />}
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='common.field.user' defaultMessage='User' />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='contacts.page.col.contactInfo' defaultMessage='Contact information' />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='contacts.page.col.phoneEmail' defaultMessage='Phone / Email' />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='common.field.dateOfBirth' defaultMessage='Date of Birth' />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='contacts.page.col.latestPolicies' defaultMessage='Latest Policies' />
                      <div className='text-muted fw-normal' style={{fontSize: '0.75rem'}}>
                        <FormattedMessage id='contacts.page.col.latestPoliciesSub' defaultMessage='Type - Year - Status - Carrier' />
                      </div>
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='common.field.actions' defaultMessage='Actions' />
                    </th>
                  </tr>
                </thead>
                {(loading || !dataAllContacts) && !isPag ? (
                  <tbody>
                    {Array.from({length: 10}).map((_, i) => (
                      <tr key={i} className='placeholder-glow'>
                        <td>
                          <span className='placeholder rounded' style={{width: 18, height: 18, display: 'inline-block'}} />
                        </td>
                        <td>
                          <span className='placeholder rounded-circle' style={{width: 32, height: 32, display: 'inline-block'}} />
                        </td>
                        <td>
                          <span className='placeholder rounded' style={{width: 150, display: 'inline-block'}} />
                          <br />
                          <span className='placeholder rounded mt-1' style={{width: 90, display: 'inline-block'}} />
                        </td>
                        <td>
                          <span className='placeholder rounded' style={{width: 110, display: 'inline-block'}} />
                          <br />
                          <span className='placeholder rounded mt-1' style={{width: 160, display: 'inline-block'}} />
                        </td>
                        <td>
                          <span className='placeholder rounded' style={{width: 90, display: 'inline-block'}} />
                        </td>
                        <td>
                          <span className='placeholder rounded' style={{width: 200, display: 'inline-block'}} />
                          <br />
                          <span className='placeholder rounded mt-1' style={{width: 200, display: 'inline-block'}} />
                        </td>
                        <td>
                          <span className='placeholder rounded' style={{width: 60, height: 28, display: 'inline-block'}} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ) : (
                  <tbody>
                    {dataArr.length === 0 && (
                      <tr>
                        <td colSpan={7}>
                          <div className='d-flex flex-center' style={{height: '30vh'}}>
                            <FormattedMessage id='contacts.page.empty' defaultMessage='There are no contacts currently added' />
                          </div>
                        </td>
                      </tr>
                    )}
                    {loading && (
                      <tr>
                        <td colSpan={7}>
                          <div className='d-flex flex-center' style={{height: '30vh'}}>
                            <Spinner animation='border' />
                          </div>
                        </td>
                      </tr>
                    )}
                    {dataArr.slice(first * page - first, first * page).map(({node}) => {
                      const entries = node.signatureEntries?.edges || []
                      const hasSigned = entries.some((e) => e.node.status === 'SIGNED')
                      const hasRequested = entries.some((e) => !!e.node.requestedOn)
                      const hasMembers = (node.members?.edges?.length || 0) > 0
                      const isDeletable = !node.lead && !node.memberPolicy && !hasMembers && entries.length === 0

                      const mostRecentMemberUser = [...(node.members?.edges || [])]
                        .filter((e) => e.node.policy?.user && e.node.policy?.created)
                        .sort(
                          (a, b) =>
                            new Date(b.node.policy!.created as string).getTime() -
                            new Date(a.node.policy!.created as string).getTime()
                        )[0]?.node.policy?.user

                      const normalizeAgent = (u: any) =>
                        u
                          ? {
                              firstName: u.firstName || '',
                              lastName: u.lastName || '',
                              email: u.email || '',
                              cellphone: u.cellphone,
                              avatar: u.avatar,
                            }
                          : null

                      const rawAgent = node.memberPolicy?.policy?.user || mostRecentMemberUser
                      const policyAgent = normalizeAgent(rawAgent)
                      const contactOwner = normalizeAgent(node.user)
                      const sameOwner =
                        !!rawAgent &&
                        !!node.user &&
                        String(parseId(rawAgent.id)) === String(parseId(node.user.id))

                      return (
                        <tr
                          key={node.id}
                          onClick={() => {
                            setInfoPreview(node)
                            onOpenPreview()
                          }}
                          style={{cursor: 'pointer'}}
                        >
                          <td>
                            <div className='set-quick-status'>
                              {canMergeContacts && (
                                <Form.Check
                                  className='me-2'
                                  checked={selectedContactIds.includes(node.id)}
                                  aria-label={`Select ${node.fullName || 'contact'}`}
                                  onClick={(event) => event.stopPropagation()}
                                  onChange={() => toggleContactSelection(node.id)}
                                />
                              )}
                              <div className='set-important'>
                                <i
                                  style={{
                                    color: node.isImportant ? 'gold' : 'inherit',
                                    cursor: 'pointer',
                                  }}
                                  className='bi bi-star-fill fs-3'
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    node.isImportant = !node.isImportant
                                    handleUpdateContact(node.id, {isImportant: node.isImportant})
                                  }}
                                />
                              </div>
                              <div>
                                {hasSigned ? (
                                  <i
                                    style={{color: 'blue', cursor: 'pointer'}}
                                    className='bi bi-vector-pen fs-4'
                                    title={intl.formatMessage({id: 'contacts.page.consentSigned', defaultMessage: 'Consent request signed'})}
                                  />
                                ) : hasRequested ? (
                                  <i
                                    style={{color: 'gray', cursor: 'pointer'}}
                                    className='bi bi-send-check fs-4'
                                    title={intl.formatMessage({id: 'contacts.page.consentSent', defaultMessage: 'Consent request sent'})}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className='d-flex align-items-center gap-1'>
                              {contactOwner && (
                                <>
                                  <UserAvatar
                                    user={contactOwner}
                                    tooltipId={`contact-owner-${parseId(node.id)}`}
                                    size={32}
                                  />
                                  <AgentInfoTooltip
                                    id={`contact-owner-${parseId(node.id)}`}
                                    title={intl.formatMessage({id: 'contacts.page.contactOwner', defaultMessage: 'Contact Owner:'})}
                                    user={contactOwner}
                                  />
                                </>
                              )}
                              {policyAgent && !sameOwner && (
                                <>
                                  <UserAvatar
                                    user={policyAgent}
                                    tooltipId={`policy-agent-${parseId(node.id)}`}
                                    size={32}
                                  />
                                  <AgentInfoTooltip
                                    id={`policy-agent-${parseId(node.id)}`}
                                    title={intl.formatMessage({id: 'contacts.page.policyAgent', defaultMessage: 'Policy Agent:'})}
                                    user={policyAgent}
                                  />
                                </>
                              )}
                              {!contactOwner && !policyAgent && (
                                <UserAvatar user={null} tooltipId={`contact-owner-${parseId(node.id)}`} size={32} />
                              )}
                            </div>
                          </td>
                          <td>
                            <div>
                              <span
                                className='text-primary text-link fw-bold'
                                style={{cursor: 'pointer'}}
                                data-tooltip-id={`myt2${parseId(node.id)}`}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setInfoPreview(node)
                                  onOpenPreview()
                                }}
                              >
                                {node.fullName}
                              </span>
                              {(node.stateEU?.code || node.zipCode) && (
                                <div className='text-muted' style={{fontSize: '0.85rem'}}>
                                  {[node.stateEU?.code, node.zipCode].filter(Boolean).join(' ')}
                                </div>
                              )}
                              {node.tagAssignments && node.tagAssignments.length > 0 && (
                                <div className='d-flex flex-wrap gap-1 mt-1'>
                                  {node.tagAssignments.map((assignment: any) => (
                                    <span
                                      key={assignment.tag.id}
                                      className='badge rounded-pill'
                                      style={{
                                        backgroundColor: assignment.tag.color,
                                        color: getTagContrastColor(assignment.tag.color),
                                        fontSize: '0.7rem',
                                      }}
                                    >
                                      {assignment.tag.name}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <td>
                            <PhoneProviderActions phone={node.phone} />
                            {node.email && (
                              <div className='text-muted' style={{fontSize: '0.85rem'}}>
                                {node.email}
                              </div>
                            )}
                          </td>
                          <td>
                            {node.dateBirth ? (
                              <>
                                <div>{moment(node.dateBirth).format('MM/DD/YYYY')}</div>
                                <div className='text-muted' style={{fontSize: '0.85rem'}}>
                                  <FormattedMessage
                                    id='contacts.page.yearsOld'
                                    defaultMessage='{years} years'
                                    values={{years: moment().diff(moment(node.dateBirth), 'years')}}
                                  />
                                </div>
                              </>
                            ) : (
                              intl.formatMessage({id: 'common.value.notAvailable', defaultMessage: 'N/A'})
                            )}
                          </td>
                          <td>
                            <ContactPolicyTypes
                              contact={node}
                              statuses={dataPolicyStatuses?.allPolicyStatuses?.edges ?? []}
                            />
                          </td>
                          <td>
                            <Dropdown as={ButtonGroup} onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant='outline-primary'
                                size='sm'
                                style={{border: '1px solid #3e97ff'}}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onOpenPreview()
                                  setInfoPreview(node)
                                }}
                              >
                                <FormattedMessage id='common.action.preview' defaultMessage='Preview' />
                              </Button>
                              <Dropdown.Toggle
                                split
                                variant='outline-primary'
                                size='sm'
                                id='dropdown-custom-2'
                                style={{border: '1px solid #3e97ff'}}
                              />
                              <Dropdown.Menu className='dropdown-menu-policy'>
                                <Dropdown.Item
                                  eventKey='1'
                                  onClick={() => {
                                    setIdContact(node.id)
                                    onOpen()
                                  }}
                                >
                                  <i className='bi bi-pencil-fill' /> <FormattedMessage id='common.action.edit' defaultMessage='Edit' />
                                </Dropdown.Item>

                                <Dropdown.Item onClick={() => setTagTargetId(node.id)}>
                                  <i className='bi bi-tags' /> <FormattedMessage id='contacts.page.manageTags' defaultMessage='Manage Tags' />
                                </Dropdown.Item>

                                {(isAdministrator(permissions) ||
                                  (node.user?.id &&
                                    String(parseId(node.user.id)) === String(parseId(currentUserId || '')))) && (
                                  <Dropdown.Item
                                    onClick={() => {
                                      setIdContact(node.id)
                                      onOpenAssignContact()
                                    }}
                                  >
                                    <i className='bi bi-person-check' /> <FormattedMessage id='contacts.assign.title' defaultMessage='Assign Contact' />
                                  </Dropdown.Item>
                                )}

                                {canViewCommunications && (
                                  <Dropdown.Item
                                    onClick={() => {
                                      setInfoPreview(node)
                                      onOpenCommunications()
                                    }}
                                  >
                                    <i className='bi bi-chat-left-text' /> <FormattedMessage id='contacts.comms.title' defaultMessage='Communication Logs' />
                                  </Dropdown.Item>
                                )}

                                {hasPermission(permissions, ALL_PERMISSIONS.VIEW_SMS) && (
                                  <Dropdown.Item
                                    onClick={() => {
                                      setInfoPreview(node)
                                      onOpenMessaging()
                                    }}
                                  >
                                    <i className='bi bi-whatsapp' /> <FormattedMessage id='contacts.page.sendNotification' defaultMessage='Send Notification' />
                                  </Dropdown.Item>
                                )}

                                {canViewSignatureForms && (
                                  hasSigned ? (
                                    <Dropdown.Item
                                      eventKey='2'
                                      onClick={() => {
                                        setIdContact(node.id)
                                        onOpenQuote()
                                      }}
                                    >
                                      <i className='bi bi-file-earmark-plus' /> <FormattedMessage id='contacts.page.createQuote' defaultMessage='Create quote' />
                                    </Dropdown.Item>
                                  ) : (
                                    <Dropdown.Item
                                      eventKey='2'
                                      onClick={() => {
                                        setIdContact(node.id)
                                        setInfoContact({
                                          hasEmail: !!node.email,
                                          hasPhone: !!node.phone,
                                        })
                                        onOpenRequest()
                                      }}
                                    >
                                      <i className='bi bi-send-check' /> <FormattedMessage id='contacts.page.requestSignature' defaultMessage='Request Signature' />
                                    </Dropdown.Item>
                                  )
                                )}

                                {isDeletable && (
                                  <>
                                    <Dropdown.Divider />
                                    <Dropdown.Item
                                      eventKey='3'
                                      onClick={() => {
                                        setContactToDelete(node)
                                      }}
                                    >
                                      <i className='bi bi-trash' /> <FormattedMessage id='common.action.delete' defaultMessage='Delete' />
                                    </Dropdown.Item>
                                  </>
                                )}
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </Col>
      </Row>

      {isOpen && (
        <ModalAddEditContact
          refetch={refetch}
          isOpen={isOpen}
          onClose={onClose}
          keyboard={false}
          backdrop='static'
          idContact={idContact}
        />
      )}
      {isOpenPreview && infoPreview && (
        <ModalPreview idContact={infoPreview.id} onClose={onClosePreview} />
      )}
      <TagManager
        show={showTagManager}
        onHide={() => setShowTagManager(false)}
        onTagsUpdated={() => handleFilters()}
      />
      {tagTargetId && (
        <BulkTagModal
          show={Boolean(tagTargetId)}
          onHide={() => setTagTargetId(null)}
          selectedContactIds={[tagTargetId]}
          onSuccess={() => {
            setTagTargetId(null)
            handleFilters()
          }}
        />
      )}
      {isOpenAssignContact && idContact && (
        <ModalAssignContact
          id={idContact}
          isOpen={isOpenAssignContact}
          onClose={onCloseAssignContact}
          refetch={() => handleFilters()}
        />
      )}
      {isOpenRequest && idContact && canViewSignatureForms && (
        <ModalRequestSignature
          keyboard={false}
          backdrop='static'
          isOpen={isOpenRequest}
          sourceId={idContact}
          sourceType='contact'
          onClose={onCloseRequest}
          refetch={refetch}
          hasEmail={infoContact?.hasEmail}
          hasPhone={infoContact?.hasPhone}
        />
      )}

      {isOpenQuote && idContact && (
        <ModalAddPolice
          refetch={refetch}
          isOpen={isOpenQuote}
          onClose={onCloseQuote}
          keyboard={false}
          backdrop='static'
          isQuote={true}
          idContact={idContact}
        />
      )}

      {isOpenCommunications && infoPreview && (
        <ModalCommunicationLogs
          idContact={infoPreview.id}
          isOpen={isOpenCommunications}
          onClose={onCloseCommunications}
        />
      )}

      {isOpenMessaging && infoPreview && (
        <SendMessageCanvas
          show={isOpenMessaging}
          onHide={onCloseMessaging}
          recipient={{
            name: (infoPreview.fullName || '').trim(),
            phone: infoPreview.phone || '',
            email: infoPreview.email || '',
          }}
          context={{
            type: 'CONTACT',
            id: infoPreview.id,
          }}
          data={{
            contact: infoPreview,
            lead: {
              ...infoPreview,
              name: infoPreview.fullName,
              first_name: infoPreview.firstName,
              last_name: infoPreview.lastName,
            },
          }}
          defaultChannel="WHATSAPP"
        />
      )}

      <Modal show={!!contactToDelete} onHide={() => setContactToDelete(null)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FormattedMessage id='contacts.page.deleteTitle' defaultMessage='Delete contact' />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h4 className='fs-2 mb-0'>
            <FormattedMessage
              id='contacts.page.deleteConfirm'
              defaultMessage='Are you sure you want to delete this contact?'
            />
          </h4>
          {contactToDelete?.fullName && (
            <p className='mt-3 mb-0 text-muted'>
              {contactToDelete.fullName}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            size='sm'
            onClick={() => setContactToDelete(null)}
            disabled={loadingDeleteContact}
          >
            <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
          </Button>
          <Button
            variant='danger'
            size='sm'
            onClick={() => {
              if (!contactToDelete?.id) return
              void handleDeleteContact(contactToDelete.id)
            }}
            disabled={loadingDeleteContact}
          >
            {loadingDeleteContact && <Spinner animation='border' size='sm' className='me-2' />}
            <FormattedMessage id='common.action.delete' defaultMessage='Delete' />
          </Button>
        </Modal.Footer>
      </Modal>

      <ModalMergeContacts
        show={showMergeContacts}
        groups={mergeGroups}
        analyzedCount={mergeAnalysis.analyzedCount}
        truncated={mergeAnalysis.truncated}
        onHide={() => setShowMergeContacts(false)}
        onMerged={() => {
          setSelectedContactIds([])
          void handleFilters()
        }}
      />


      {isOpenFilters && (
         <ModalFilters
          isOpen={isOpenFilters}
          onClose={onCloseFilters}
          onApply={handleFilterApply}
          filters={formData}
          setFilters={setFormData}
          savedFilters={savedContactFilters}
          onSaveFilter={handleSaveContactFilter}
          onUpdateFilter={handleUpdateContactFilter}
          onDeleteFilter={handleDeleteContactFilter}
          {...filterData}
        />

      )}
    </div>
  )
}
